import { useEffect, useState } from 'react'
import { translate, type Lang } from './i18n'
import type { ApprovalPolicyView, DeviationPolicyView, FormationPolicyView, PolicyChange } from './api'
import { fmtDateTime } from './time'
import { fmtDistance, shortLength } from './units'

/** Lo que se guarda desde la tarjeta de conformación. */
export interface FormationPolicyChange {
  rookieThresholdYears: number
  severities: Record<string, string>
  requiredCertificationsByShift: Record<string, string[]>
  reason: string
}

const SEVERITIES = ['Soft', 'Hard', 'Blocking'] as const

/** «fto, k9 » → ["FTO", "K9"]: como las guarda el servidor. */
export const parseCertifications = (text: string): string[] =>
  [...new Set(text.split(',').map((c) => c.trim().toUpperCase()).filter((c) => c.length > 0))]

/**
 * Las reglas de la agencia: umbrales de desvío, política de aprobación y reglas de conformación.
 *
 * Las tres estaban compiladas. Los umbrales decidían cuándo se le marca un desvío a un oficial, y
 * la regla de los cuatro ojos dejaba sin salida a la agencia chica donde el que planifica es el
 * que manda. Ninguna de las dos es decisión del programador.
 *
 * Todo cambio pide motivo y queda en bitácora, porque son reglas que se aplican sobre personas: la
 * pregunta que viene después es «¿quién movió la vara, y con qué argumento?».
 */
export function AgencyRules({
  lang, deviation, approval, busy, onSaveDeviation, onSaveApproval,
  formation = null, shiftCodes = [], onSaveFormation,
}: {
  lang: Lang
  deviation: DeviationPolicyView | null
  approval: ApprovalPolicyView | null
  formation?: FormationPolicyView | null
  /** Los turnos de la agencia: a cada uno se le pueden exigir certificaciones. */
  shiftCodes?: string[]
  onSaveFormation?: (p: FormationPolicyChange) => void
  busy: boolean
  onSaveDeviation: (p: {
    lateArrivalMinutes: number; minPresenceRatio: number; geofenceMeters: number; reason: string
  }) => void
  onSaveApproval: (p: {
    requiresApproval: boolean; requireDifferentApprover: boolean; reason: string
  }) => void
}) {
  const [late, setLate] = useState(0)
  const [presence, setPresence] = useState(0)
  const [geofence, setGeofence] = useState(0)
  const [porQue, setPorQue] = useState('')

  const [requiere, setRequiere] = useState(true)
  const [distinto, setDistinto] = useState(true)
  const [porQueAprobacion, setPorQueAprobacion] = useState('')

  const [novato, setNovato] = useState(1)
  const [gravedades, setGravedades] = useState<Record<string, string>>({})
  const [certs, setCerts] = useState<Record<string, string>>({})
  const [porQueConformacion, setPorQueConformacion] = useState('')

  useEffect(() => {
    if (!formation) return
    setNovato(formation.rookieThresholdYears)
    setGravedades({ ...formation.severities })
    setCerts(Object.fromEntries(Object.entries(formation.requiredCertificationsByShift).map(([k, v]) => [k, v.join(', ')])))
    setPorQueConformacion('')
  }, [formation])

  // Los turnos que existen y, además, cualquiera que la política ya mencione aunque hoy no exista:
  // esconderlo dejaría una exigencia vigente que nadie ve.
  const turnos = [...new Set([...shiftCodes, ...Object.keys(formation?.requiredCertificationsByShift ?? {})])]

  // Lo que rige manda mientras no se esté editando; guardar recarga y vuelve a caer aquí.
  useEffect(() => {
    if (!deviation) return
    setLate(deviation.current.lateArrivalMinutes)
    setPresence(deviation.current.minPresenceRatio)
    setGeofence(deviation.current.geofenceMeters)
    setPorQue('')
  }, [deviation])

  useEffect(() => {
    if (!approval) return
    setRequiere(approval.requiresApproval)
    setDistinto(approval.requireDifferentApprover)
    setPorQueAprobacion('')
  }, [approval])

  const cuando = (iso: string) => fmtDateTime(iso, lang)

  // Lo fijado, escrito para quien lee: con los nombres de las reglas y en las unidades de la
  // agencia. Venía armado del servidor con los códigos internos en inglés y en metros (H-75).
  const umbrales = (late: number, presence: number, geofence: number) =>
    `${late} min · ${presence} · ${fmtDistance(geofence, lang)}`
  const linea = (h: PolicyChange): string => {
    const d = h.detail
    if (!d) return h.values
    if (h.kind === 'deviation')
      return umbrales(Number(d['late']), Number(d['presence']), Number(d['geofenceMeters']))
    if (h.kind === 'approval') return translate(lang, `policyLog.${d['mode'] ?? ''}`)
    if (h.kind === 'formation') {
      const reglas = Object.entries(d)
        .filter(([k]) => k.startsWith('rule.'))
        .map(([k, v]) => `${translate(lang, k)}: ${translate(lang, `severity.${v}`)}`)
      const certs = Object.entries(d)
        .filter(([k]) => k.startsWith('certs.'))
        .map(([k, v]) => translate(lang, 'policyLog.certs', [k.slice(6), v]))
      return [translate(lang, 'policyLog.rookie', [d['rookie'] ?? '']), ...reglas, ...certs].join(' · ')
    }
    return h.values
  }

  // Una sola línea de tiempo con las tres: quien revisa pregunta qué se cambió, no en qué tabla.
  const historia: PolicyChange[] = [...(deviation?.history ?? []), ...(approval?.history ?? []), ...(formation?.history ?? [])]
    .sort((a, b) => b.at.localeCompare(a.at))

  return (
    <section className="panel">
      <h2>{translate(lang, 'policy.title')}</h2>
      <p className="notice">{translate(lang, 'policy.help')}</p>

      <div className="card">
        <h3>{translate(lang, 'policy.deviation')}</h3>
        <p className="notice">{translate(lang, 'policy.deviationHelp')}</p>

        <label className="rule">
          {translate(lang, 'policy.late')}
          <input
            type="number" min={1} max={120} value={late} disabled={busy}
            onChange={(e) => setLate(Number(e.target.value))}
          />
        </label>
        <label className="rule">
          {translate(lang, 'policy.presence')}
          <input
            type="number" min={0.05} max={1} step={0.01} value={presence} disabled={busy}
            onChange={(e) => setPresence(Number(e.target.value))}
          />
        </label>
        <label className="rule">
          {translate(lang, 'policy.geofence', [translate(lang, `units.${shortLength.unit()}`)])}
          {/* En la unidad de la agencia (H-64); se guarda en metros. */}
          <input
            type="number" min={shortLength.fromMeters(25)} max={shortLength.fromMeters(5000)}
            step={shortLength.unit() === 'ft' ? 50 : 25} value={shortLength.fromMeters(geofence)} disabled={busy}
            onChange={(e) => setGeofence(shortLength.toMeters(Number(e.target.value)))}
          />
        </label>

        <label className="rule">
          {translate(lang, 'policy.reason')}
          <input
            value={porQue} disabled={busy} placeholder={translate(lang, 'policy.reasonHelp')}
            onChange={(e) => setPorQue(e.target.value)}
          />
        </label>

        {deviation && (
          <p className="notice">
            {/* Lo que rige ahora, dicho: el turno en curso se sigue midiendo con esto aunque haya un
                cambio fijado (PD-05b). Los campos, al editarlos, dejan de decirlo. */}
            {translate(lang, 'policy.current', [umbrales(deviation.current.lateArrivalMinutes,
              deviation.current.minPresenceRatio, deviation.current.geofenceMeters)])}
            {' '}
            {translate(lang, 'policy.effective', [cuando(deviation.nextShiftStart)])}
            {deviation.pending && deviation.pendingFrom && (
              <>
                {' '}
                {translate(lang, 'policy.pending', [
                  cuando(deviation.pendingFrom),
                  umbrales(deviation.pending.lateArrivalMinutes, deviation.pending.minPresenceRatio, deviation.pending.geofenceMeters),
                ])}
              </>
            )}
          </p>
        )}

        <div className="actions">
          <button
            className="primary"
            disabled={busy || porQue.trim() === ''}
            title={porQue.trim() === '' ? translate(lang, 'policy.reasonNeeded') : undefined}
            onClick={() => onSaveDeviation({
              lateArrivalMinutes: late,
              minPresenceRatio: presence,
              geofenceMeters: geofence,
              reason: porQue.trim(),
            })}
          >
            {translate(lang, 'config.save')}
          </button>
        </div>
      </div>

      <div className="card">
        <h3>{translate(lang, 'policy.approval')}</h3>
        <p className="notice">{translate(lang, 'policy.approvalHelp')}</p>

        <label className="check">
          <input
            type="checkbox" checked={requiere} disabled={busy}
            onChange={(e) => {
              setRequiere(e.target.checked)
              // Sin aprobación no hay quien firme: exigir que firme otro no significa nada.
              if (!e.target.checked) setDistinto(false)
            }}
          />
          {translate(lang, 'policy.requires')}
        </label>

        <label className="check">
          <input
            type="checkbox" checked={distinto} disabled={busy || !requiere}
            onChange={(e) => setDistinto(e.target.checked)}
          />
          {translate(lang, 'policy.different')}
        </label>
        <p className="notice">{translate(lang, 'policy.differentHelp')}</p>

        <label className="rule">
          {translate(lang, 'policy.reason')}
          <input
            value={porQueAprobacion} disabled={busy} placeholder={translate(lang, 'policy.reasonHelpApproval')}
            onChange={(e) => setPorQueAprobacion(e.target.value)}
          />
        </label>

        <div className="actions">
          <button
            className="primary"
            disabled={busy || porQueAprobacion.trim() === ''}
            title={porQueAprobacion.trim() === '' ? translate(lang, 'policy.reasonNeeded') : undefined}
            onClick={() => onSaveApproval({
              requiresApproval: requiere,
              requireDifferentApprover: distinto,
              reason: porQueAprobacion.trim(),
            })}
          >
            {translate(lang, 'config.save')}
          </button>
        </div>
      </div>

      {formation && onSaveFormation && (
        <div className="card">
          <h3>{translate(lang, 'policy.formation')}</h3>
          <p className="notice">{translate(lang, 'policy.formationHelp')}</p>

          <label className="rule">
            {translate(lang, 'policy.rookie')}
            <input
              type="number" min={0} max={10} step={0.5} value={novato} disabled={busy}
              onChange={(e) => setNovato(Number(e.target.value))}
            />
          </label>

          {formation.configurable.map((rule) => (
            <label className="rule" key={rule}>
              {translate(lang, `rule.${rule}`)}
              <select
                value={gravedades[rule] ?? 'Hard'} disabled={busy}
                onChange={(e) => setGravedades({ ...gravedades, [rule]: e.target.value })}
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>{translate(lang, `severity.${s}`)}</option>
                ))}
              </select>
            </label>
          ))}
          <p className="notice">{translate(lang, 'policy.blockingAlways')}</p>

          {turnos.map((code) => (
            <label className="rule" key={code}>
              {translate(lang, 'policy.certsFor', [code])}
              <input
                value={certs[code] ?? ''} disabled={busy} placeholder="FTO, K9"
                onChange={(e) => setCerts({ ...certs, [code]: e.target.value })}
              />
            </label>
          ))}

          <label className="rule">
            {translate(lang, 'policy.reason')}
            <input
              value={porQueConformacion} disabled={busy} placeholder={translate(lang, 'policy.reasonHelpFormation')}
              onChange={(e) => setPorQueConformacion(e.target.value)}
            />
          </label>
          <p className="notice">{translate(lang, 'policy.formationEffective')}</p>

          <div className="actions">
            <button
              className="primary"
              disabled={busy || porQueConformacion.trim() === ''}
              title={porQueConformacion.trim() === '' ? translate(lang, 'policy.reasonNeeded') : undefined}
              onClick={() => onSaveFormation({
                rookieThresholdYears: novato,
                severities: gravedades,
                requiredCertificationsByShift: Object.fromEntries(
                  Object.entries(certs)
                    .map(([k, v]): [string, string[]] => [k, parseCertifications(v)])
                    .filter(([, v]) => v.length > 0)),
                reason: porQueConformacion.trim(),
              })}
            >
              {translate(lang, 'config.save')}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <h3>{translate(lang, 'policy.history')}</h3>
        {historia.length === 0
          ? <p className="notice">{translate(lang, 'policy.noHistory')}</p>
          : (
            <table className="compare">
              <thead>
                <tr>
                  <th>{translate(lang, 'policy.when')}</th>
                  <th>{translate(lang, 'policy.who')}</th>
                  <th>{translate(lang, 'policy.what')}</th>
                  <th>{translate(lang, 'policy.why')}</th>
                </tr>
              </thead>
              <tbody>
                {historia.map((h) => (
                  <tr key={`${h.at}-${h.values}`}>
                    <td>{cuando(h.at)}</td>
                    <td>{h.changedBy}</td>
                    <td>{linea(h)}</td>
                    <td>{h.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </section>
  )
}
